import uniqBy from 'lodash/uniqBy';

import { formatPeriod } from './formatMonthList';
import { periodKey, periodsNotHeld } from './periods';
import { isFiledOnAnotherAccount, thisAccountLabel } from './statementFilings';

import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';
import { UploadedStatementStatus } from 'services/bookkeeping/queries/useStatementUploadStatuses';

/**
 * Where one file holds tracker rows, as one account card's window sees them — what a removal
 * from that card leaves behind.
 *
 * The server deletes a file once it holds no row on any account, so this is the whole input to
 * "does this remove the file?". Rows on a card the file's pages don't name appear in neither
 * source below, but the only way to make one is the month menu, which is withheld from a file
 * filed on another card (and the server refuses it) — so no reachable file has one.
 */
export interface FileHoldings {
    /** Months the file holds on this card, from coverage and the statuses read together. */
    readonly monthsHere: StatementPeriod[];
    /** The file holds a row on at least one other card. */
    readonly holdsElsewhere: boolean;
}

interface FileHoldingsArgs {
    readonly status: UploadedStatementStatus | undefined;
    /** The window's card. Null: a removal there names no card and takes the whole file. */
    readonly bankAccountId: string | null;
    /** The file's rows on this card from coverage, including the rows a staged edit names. */
    readonly rowsHere: StatementPeriod[];
}

/**
 * Both reads, unioned, because they refresh on different clocks: the statuses run ahead of
 * coverage while a fresh upload settles, and either alone would under-count the file's rows.
 */
export function fileHoldings({ status, bankAccountId, rowsHere }: FileHoldingsArgs): FileHoldings {
    if (bankAccountId === null) {
        return { monthsHere: [], holdsElsewhere: false };
    }
    const filings = status?.statement_filings ?? [];
    // `periods` is the home card's rows, so it only speaks for this card when this is the home card.
    const isHomeHere = status?.bank_account_id === bankAccountId;
    const months = [
        ...rowsHere,
        ...(isHomeHere ? status.periods : []),
        ...filings.filter((filing) => filing.outcome === 'filed' && filing.bank_account_id === bankAccountId),
    ];
    const isHomeElsewhere = status?.bank_account_id != null && !isHomeHere && status.periods.length > 0;
    return {
        monthsHere: uniqBy(months, periodKey),
        holdsElsewhere: isHomeElsewhere || isFiledOnAnotherAccount(filings, bankAccountId),
    };
}

/**
 * Whether taking `removed` off this card leaves the file holding nothing anywhere, so the server
 * deletes it. Without `removed`, every month the file holds here goes.
 *
 * Unknown holdings read as a deletion: a label that warns of one that doesn't happen is the
 * safer of the two ways to be wrong.
 */
export function removalDeletesFile(holdings: FileHoldings | undefined, removed?: StatementPeriod): boolean {
    if (!holdings) {
        return true;
    }
    if (holdings.holdsElsewhere) {
        return false;
    }
    return removed ? periodsNotHeld(holdings.monthsHere, [removed]).length === 0 : true;
}

interface RemoveControlLabelArgs {
    readonly fileName: string;
    /** The month the row shows. Absent on a row standing for the whole file. */
    readonly period?: StatementPeriod;
    readonly accountMask: string;
    readonly deletesFile: boolean;
}

/**
 * The remove control's accessible name, which says what it takes: one month off this card —
 * "Remove March 2026 from ··5555" — and, when that is the last thing the file holds anywhere,
 * that the file goes with it.
 */
export function removeControlLabel({ fileName, period, accountMask, deletesFile }: RemoveControlLabelArgs): string {
    const card = thisAccountLabel(accountMask);
    if (!period) {
        return deletesFile ? `Delete ${fileName}` : `Remove ${fileName} from ${card}`;
    }
    const removal = `Remove ${formatPeriod(period)} from ${card}`;
    return deletesFile ? `${removal} and delete ${fileName}` : removal;
}
